import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { ArrowDownRight, ArrowUpRight, FileSpreadsheet, TrendingDown, TrendingUp, AlertTriangle, CalendarDays, ExternalLink, Info, Plus, Trash2, Wallet, Upload } from "lucide-react";
import { ComposedChart, Bar, Line, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer, Legend, CartesianGrid } from "recharts";

const ORO = "#C9A96A";
const CREMA = "#EAD8C0";
const VERDE = "#4ADE80";
const ROJO = "#F87171";

const mxn = (n) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 0 }).format(n || 0);
const mxn2 = (n) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", minimumFractionDigits: 2 }).format(n || 0);
const fDate = (s) => (s ? s.slice(0, 10).split("-").reverse().join("/") : "—");
const corto = (n) => {
  if (n == null) return "—";
  const a = Math.abs(n);
  if (a >= 1e6) return `$${(n / 1e6).toLocaleString("es-MX", { maximumFractionDigits: 1 })} M`;
  if (a >= 1e3) return `$${(n / 1e3).toLocaleString("es-MX", { maximumFractionDigits: 0 })} K`;
  return mxn(n);
};

const PERIODOS = [
  ["semana", "Esta semana"], ["mes", "Este mes"], ["trimestre", "Este trimestre"],
  ["anio", "Este año"], ["rango", "Rango personalizado"],
];

function Tarjeta({ titulo, valor, formula, children, onClick, testId, color = "text-[#F5EFE4]", borde = "border-[#2C2822]" }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId} disabled={!onClick}
      className={`text-left bg-gradient-to-b from-[#1B1915] to-[#141210] border ${borde} rounded-xl p-4 transition-colors ${onClick ? "hover:border-[#C9A96A]/60 cursor-pointer" : "cursor-default"}`}>
      <p className="text-[10px] uppercase tracking-[0.15em] text-white/40 font-semibold flex items-center gap-1.5">
        {titulo}
        {formula && <Info size={11} className="text-white/25" title={formula} />}
      </p>
      <p className={`font-display text-2xl font-bold mt-1 ${color}`}>{valor}</p>
      {children}
    </button>
  );
}

function Variacion({ monto, pct }) {
  if (monto == null) return null;
  const up = monto >= 0;
  return (
    <p className={`text-[11px] flex items-center gap-1 mt-0.5 ${up ? "text-emerald-400" : "text-red-400"}`}>
      {up ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
      {up ? "+" : ""}{corto(monto)}{pct != null ? ` (${pct > 0 ? "+" : ""}${pct}%)` : ""} vs. periodo anterior
    </p>
  );
}

function Barras({ datos, testId }) {
  if (!datos?.length) return <p className="text-sm text-white/30" data-testid={testId}>Sin movimientos por categoría en este periodo.</p>;
  const max = Math.max(...datos.map((d) => Math.max(d.real, d.programado))) || 1;
  return (
    <div className="space-y-2.5" data-testid={testId}>
      {datos.map((d) => (
        <div key={d.categoria} data-testid={`flujo-cat-${d.categoria.replace(/\s+/g, "-")}`}>
          <div className="flex justify-between text-[11px] mb-1">
            <span className="text-white/60 first-letter:uppercase">{d.categoria}</span>
            <span className="text-[#EAD8C0]">{corto(d.real)}{d.programado > 0 && <span className="text-white/30"> · prog. {corto(d.programado)}</span>}</span>
          </div>
          <div className="h-2 rounded-full bg-white/5 overflow-hidden flex">
            <div className="h-full" style={{ width: `${(d.real / max) * 100}%`, background: ORO }} />
            <div className="h-full" style={{ width: `${(d.programado / max) * 100}%`, background: "#4B4436" }} />
          </div>
        </div>
      ))}
      <p className="text-[10px] text-white/25 pt-1">Barra dorada = cobrado/pagado real · barra oscura = programado del periodo</p>
    </div>
  );
}

function Tendencia({ datos, colorReal, testId }) {
  if (!datos?.length) return null;
  return (
    <ResponsiveContainer width="100%" height={200}>
      <ComposedChart data={datos} data-testid={testId}>
        <CartesianGrid stroke="#2C2822" vertical={false} />
        <XAxis dataKey="etiqueta" tick={{ fontSize: 9, fill: "#8B857A" }} />
        <YAxis tick={{ fontSize: 9, fill: "#8B857A" }} width={48} tickFormatter={(v) => `$${(v / 1e6).toFixed(1)}M`} />
        <RTooltip formatter={(v, n) => [mxn(v), n === "real" ? "Real" : "Programado"]}
          contentStyle={{ background: "#1B1915", border: "1px solid #2C2822", borderRadius: 8, fontSize: 12, color: "#F5EFE4" }} />
        <Legend formatter={(v) => <span style={{ color: "#8B857A", fontSize: 11 }}>{v === "real" ? "Real" : "Programado"}</span>} />
        <Bar dataKey="real" fill={colorReal} radius={[3, 3, 0, 0]} maxBarSize={38} />
        <Line type="linear" dataKey="programado" stroke={CREMA} strokeWidth={1.8} strokeDasharray="5 4" dot={{ r: 2 }} />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

function DetalleModal({ q, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!q) return;
    setData(null);
    api.get(`/flujo/movimientos${q.query}`).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  }, [q]);
  if (!q) return null;
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl max-h-[88vh] overflow-y-auto" data-testid="flujo-detalle-modal">
        <DialogHeader><DialogTitle className="font-display text-[#002B49]">{q.titulo}</DialogTitle></DialogHeader>
        <p className="sr-only">Listado de los movimientos que componen este indicador.</p>
        {!data ? <p className="text-sm text-slate-400">Cargando movimientos…</p> : data.movimientos.length === 0 ? (
          <p className="text-sm text-slate-500" data-testid="flujo-detalle-vacio">Sin movimientos en este corte.</p>
        ) : (
          <>
            <p className="text-xs text-slate-500">{data.movimientos.length} movimiento(s) · total <b className="text-[#002B49]">{mxn2(data.total)}</b> · periodo {data.periodo}</p>
            <div className="overflow-x-auto border rounded-xl">
              <table className="w-full text-xs min-w-[860px]">
                <thead><tr className="bg-slate-50 text-left text-[10px] uppercase tracking-wider text-slate-500">
                  <th className="px-3 py-2">Categoría</th><th className="px-3 py-2">Contraparte</th>
                  <th className="px-3 py-2">Referencia</th><th className="px-3 py-2">Proyecto</th>
                  <th className="px-3 py-2 text-right">Monto</th><th className="px-3 py-2">Programada</th>
                  <th className="px-3 py-2">Real</th><th className="px-3 py-2">Estatus</th><th className="px-3 py-2">Origen</th></tr></thead>
                <tbody>
                  {data.movimientos.map((m) => (
                    <tr key={m.id_origen + m.estatus} className="border-b border-slate-100" data-testid={`flujo-mov-${m.id_origen}`}>
                      <td className="px-3 py-1.5 first-letter:uppercase text-slate-700">{m.categoria}</td>
                      <td className="px-3 py-1.5 text-slate-600">{m.contraparte}</td>
                      <td className="px-3 py-1.5 text-slate-500">{m.referencia}{m.nota && <span className="block text-[10px] text-slate-400">{m.nota}</span>}</td>
                      <td className="px-3 py-1.5 text-slate-500">{m.proyecto_nombre}</td>
                      <td className="px-3 py-1.5 text-right font-semibold text-[#002B49]">{mxn2(m.monto)}</td>
                      <td className="px-3 py-1.5 text-slate-500">{fDate(m.fecha_programada)}</td>
                      <td className="px-3 py-1.5 text-slate-500">{fDate(m.fecha_real)}</td>
                      <td className="px-3 py-1.5">
                        <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${m.estatus === "realizado" ? "bg-emerald-50 text-emerald-700" : m.estatus === "vencido" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"}`}>{m.estatus}</span>
                      </td>
                      <td className="px-3 py-1.5">
                        {m.link ? <a href={m.link} className="text-[#00A3B5] hover:underline flex items-center gap-1">{m.tabla_origen}<ExternalLink size={11} /></a> : m.tabla_origen}
                      </td>
                    </tr>))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CajaModal({ open, onClose, onSaved }) {
  const [data, setData] = useState(null);
  const [f, setF] = useState({ property_id: "", monto: "", fecha: new Date().toISOString().slice(0, 10), notas: "" });
  const load = () => api.get("/flujo/caja-inicial").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { if (open) load(); }, [open]); // eslint-disable-line
  if (!open) return null;
  const guardar = async (e) => {
    e.preventDefault();
    try {
      await api.put("/flujo/caja-inicial", { property_id: f.property_id || null, monto: Number(f.monto),
        fecha: f.fecha, notas: f.notas });
      toast.success("Saldo inicial de caja guardado");
      setF({ ...f, monto: "", notas: "" });
      load(); onSaved();
    } catch (err) { toast.error(apiError(err)); }
  };
  const borrar = async (pid) => {
    try {
      await api.delete(`/flujo/caja-inicial${pid ? `?property_id=${pid}` : ""}`);
      load(); onSaved();
    } catch (err) { toast.error(apiError(err)); }
  };
  const nombre = (pid) => (pid ? (data?.proyectos || []).find((p) => p.id === pid)?.nombre || pid : "Corporativo (sin proyecto)");
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="caja-modal">
        <DialogHeader><DialogTitle className="font-display text-[#002B49]">Saldo inicial de caja</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">Captura el saldo real del banco a una fecha de corte. El calendario de 8 semanas parte de ese saldo más el flujo real registrado después del corte.</p>
        <form onSubmit={guardar} className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label className="text-xs">Proyecto</Label>
            <Select value={f.property_id || "corp"} onValueChange={(v) => setF({ ...f, property_id: v === "corp" ? "" : v })}>
              <SelectTrigger data-testid="caja-proyecto-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="corp">Corporativo (sin proyecto)</SelectItem>
                {(data?.proyectos || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>)}
              </SelectContent>
            </Select></div>
          <div className="space-y-1.5"><Label className="text-xs">Fecha de corte *</Label>
            <Input type="date" required value={f.fecha} onChange={(e) => setF({ ...f, fecha: e.target.value })} data-testid="caja-fecha-input" />
            <p className="text-[10px] text-slate-400">El saldo es al cierre de ese día; el sistema suma el flujo real posterior.</p></div>
          <div className="space-y-1.5"><Label className="text-xs">Saldo en banco (MXN) *</Label>
            <Input type="number" step="0.01" required value={f.monto} onChange={(e) => setF({ ...f, monto: e.target.value })} data-testid="caja-monto-input" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Notas</Label>
            <Input value={f.notas} onChange={(e) => setF({ ...f, notas: e.target.value })} placeholder="Ej. Cuenta BBVA 1234" /></div>
          <div className="col-span-2 flex justify-end">
            <Button type="submit" className="bg-[#002B49] hover:bg-[#00395f] text-white" data-testid="caja-guardar-btn">Guardar saldo</Button>
          </div>
        </form>
        <div className="border rounded-xl overflow-hidden">
          <table className="w-full text-xs">
            <thead><tr className="bg-slate-50 text-left text-[10px] uppercase tracking-wider text-slate-500">
              <th className="px-3 py-2">Proyecto</th><th className="px-3 py-2">Corte</th>
              <th className="px-3 py-2 text-right">Saldo</th><th className="px-3 py-2">Notas</th><th className="px-3 py-2" /></tr></thead>
            <tbody>
              {(data?.saldos || []).length === 0 && <tr><td colSpan={5} className="px-3 py-4 text-center text-slate-400">Sin saldos capturados.</td></tr>}
              {(data?.saldos || []).map((s) => (
                <tr key={s.id} className="border-t border-slate-100" data-testid={`caja-row-${s.property_id || "corp"}`}>
                  <td className="px-3 py-1.5 text-slate-700">{nombre(s.property_id)}</td>
                  <td className="px-3 py-1.5 text-slate-500">{fDate(s.fecha)}</td>
                  <td className="px-3 py-1.5 text-right font-semibold text-[#002B49]">{mxn2(s.monto)}</td>
                  <td className="px-3 py-1.5 text-slate-400">{s.notas || "—"}</td>
                  <td className="px-3 py-1.5 text-right">
                    <button onClick={() => borrar(s.property_id)} className="text-slate-300 hover:text-red-500"><Trash2 size={13} /></button>
                  </td>
                </tr>))}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const CAT_EGRESO = ["proveedor", "nómina", "comisión de ventas", "terreno", "administrativo", "marketing", "impuestos", "otro egreso"];

function EgresosOperativosModal({ open, onClose, onSaved, qs, proyectos }) {
  const [lista, setLista] = useState(null);
  const [imp, setImp] = useState(null);
  const [subiendo, setSubiendo] = useState(false);
  const [f, setF] = useState({ categoria: "proveedor", concepto: "", monto: "", contraparte: "", property_id: "",
    fecha_programada: new Date().toISOString().slice(0, 10), fecha_real: "", referencia: "" });
  const load = () => api.get(`/egresos-operativos${qs}`).then((r) => setLista(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { if (open) load(); }, [open, qs]); // eslint-disable-line
  if (!open) return null;
  const guardar = async (e) => {
    e.preventDefault();
    try {
      await api.post("/egresos-operativos", { ...f, monto: Number(f.monto), property_id: f.property_id || null,
        fecha_programada: f.fecha_programada || null, fecha_real: f.fecha_real || null });
      toast.success("Egreso operativo capturado. Si su fecha queda fuera del periodo seleccionado, cambia el periodo para verlo.");
      setF({ ...f, concepto: "", monto: "", contraparte: "", referencia: "", fecha_real: "" });
      load(); onSaved();
    } catch (err) { toast.error(apiError(err)); }
  };
  const pagar = async (x) => {
    try { await api.post(`/egresos-operativos/${x.id}/pagar`, { fecha_real: new Date().toISOString().slice(0, 10) }); load(); onSaved(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const borrar = async (x) => {
    if (!window.confirm(`¿Eliminar "${x.concepto}"?`)) return;
    try { await api.delete(`/egresos-operativos/${x.id}`); load(); onSaved(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const plantilla = async () => {
    try {
      const r = await api.get("/egresos-operativos/plantilla", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "plantilla_egresos_operativos.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error("No se pudo descargar la plantilla"); }
  };
  const subir = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setSubiendo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/egresos-operativos/importar", fd);
      setImp(r.data);
      toast.success(`${r.data.resumen.validas} de ${r.data.resumen.total} filas listas para capturar`);
    } catch (err) { toast.error(apiError(err)); }
    setSubiendo(false);
  };
  const aplicar = async () => {
    try {
      const r = await api.post(`/egresos-operativos/importar/${imp.import_id}/aplicar`);
      toast.success(`${r.data.creados} egresos capturados por ${mxn2(r.data.monto)}`);
      setImp(null); load(); onSaved();
    } catch (err) { toast.error(apiError(err)); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl max-h-[88vh] overflow-y-auto" data-testid="egresos-op-modal">
        <DialogHeader><DialogTitle className="font-display text-[#002B49]">Egresos operativos — captura</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">Proveedores, nómina, comisiones, terreno, administrativo, marketing e impuestos. Si capturas fecha de pago cuenta como egreso REAL; si solo capturas vencimiento, cuenta como PROGRAMADO.</p>

        <div className="border border-dashed border-slate-300 rounded-xl p-3 flex flex-wrap items-center gap-3 bg-slate-50/60">
          <FileSpreadsheet size={16} className="text-[#00A3B5]" />
          <p className="text-xs text-slate-600 flex-1 min-w-[220px]">
            <b>Carga masiva:</b> sube el Excel del contador y capturamos todo el mes de golpe.
            <button onClick={plantilla} className="text-[#00A3B5] hover:underline ml-1" data-testid="egresos-plantilla-btn">Descargar plantilla</button>
          </p>
          <label className="cursor-pointer bg-[#002B49] hover:bg-[#00395f] text-white text-xs font-bold px-3 py-2 rounded-md flex items-center gap-2" data-testid="egresos-importar-label">
            <Upload size={13} /> {subiendo ? "Leyendo archivo…" : "Subir Excel"}
            <input type="file" accept=".xlsx,.xlsm" className="hidden" onChange={subir} data-testid="egresos-importar-input" />
          </label>
        </div>

        {imp && (
          <div className="border rounded-xl p-3 space-y-2" data-testid="egresos-import-preview">
            <div className="flex flex-wrap items-center gap-3 text-xs">
              <p className="text-slate-600 flex-1"><b>{imp.archivo}</b> · {imp.resumen.total} filas leídas ·
                <span className="text-emerald-600 font-bold"> {imp.resumen.validas} válidas ({mxn2(imp.resumen.monto_valido)})</span>
                {imp.resumen.con_error > 0 && <span className="text-red-600 font-bold"> · {imp.resumen.con_error} con error</span>}
              </p>
              <Button variant="outline" onClick={() => setImp(null)} className="h-8 text-xs" data-testid="egresos-import-cancelar">Cancelar</Button>
              <Button onClick={aplicar} disabled={!imp.resumen.validas} className="h-8 text-xs bg-[#00A3B5] hover:bg-[#008d9c] text-white" data-testid="egresos-import-aplicar">
                Capturar {imp.resumen.validas} egresos
              </Button>
            </div>
            <div className="max-h-56 overflow-y-auto border rounded-lg">
              <table className="w-full text-[11px]">
                <thead className="sticky top-0"><tr className="bg-slate-50 text-left text-[10px] uppercase tracking-wider text-slate-500">
                  <th className="px-2 py-1.5">Fila</th><th className="px-2 py-1.5">Categoría</th><th className="px-2 py-1.5">Concepto</th>
                  <th className="px-2 py-1.5 text-right">Monto</th><th className="px-2 py-1.5">Vence</th><th className="px-2 py-1.5">Pagado</th>
                  <th className="px-2 py-1.5">Proyecto</th><th className="px-2 py-1.5">Resultado</th></tr></thead>
                <tbody>
                  {imp.filas.map((r) => (
                    <tr key={r.fila} className={`border-t border-slate-100 ${r.ok ? "" : "bg-red-50/60"}`} data-testid={`egresos-import-fila-${r.fila}`}>
                      <td className="px-2 py-1 text-slate-400">{r.fila}</td>
                      <td className="px-2 py-1 first-letter:uppercase text-slate-700">{r.datos.categoria || "—"}</td>
                      <td className="px-2 py-1 text-slate-600">{r.datos.concepto || "—"}</td>
                      <td className="px-2 py-1 text-right text-slate-700">{r.datos.monto ? mxn2(r.datos.monto) : "—"}</td>
                      <td className="px-2 py-1 text-slate-500">{fDate(r.datos.fecha_programada)}</td>
                      <td className="px-2 py-1 text-slate-500">{fDate(r.datos.fecha_real)}</td>
                      <td className="px-2 py-1 text-slate-500">{r.datos.proyecto_nombre}</td>
                      <td className="px-2 py-1">{r.ok
                        ? <span className="text-emerald-700 font-bold">Lista</span>
                        : <span className="text-red-600">{r.errores.join("; ")}</span>}</td>
                    </tr>))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        <form onSubmit={guardar} className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="space-y-1.5"><Label className="text-xs">Categoría *</Label>
            <Select value={f.categoria} onValueChange={(v) => setF({ ...f, categoria: v })}>
              <SelectTrigger data-testid="egreso-categoria-select"><SelectValue /></SelectTrigger>
              <SelectContent>{CAT_EGRESO.map((c) => <SelectItem key={c} value={c} className="first-letter:uppercase">{c}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="space-y-1.5 md:col-span-2"><Label className="text-xs">Concepto *</Label>
            <Input required value={f.concepto} onChange={(e) => setF({ ...f, concepto: e.target.value })}
              placeholder="Ej. Suministro de acero — factura A-1042" data-testid="egreso-concepto-input" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Monto (MXN) *</Label>
            <Input type="number" step="0.01" min="0.01" required value={f.monto} onChange={(e) => setF({ ...f, monto: e.target.value })} data-testid="egreso-monto-input" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Contraparte</Label>
            <Input value={f.contraparte} onChange={(e) => setF({ ...f, contraparte: e.target.value })} placeholder="Proveedor / beneficiario" data-testid="egreso-contraparte-input" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Proyecto</Label>
            <Select value={f.property_id || "corp"} onValueChange={(v) => setF({ ...f, property_id: v === "corp" ? "" : v })}>
              <SelectTrigger data-testid="egreso-proyecto-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="corp">Corporativo (sin proyecto)</SelectItem>
                {proyectos.map((p) => <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>)}
              </SelectContent>
            </Select></div>
          <div className="space-y-1.5"><Label className="text-xs">Vence el</Label>
            <Input type="date" value={f.fecha_programada} onChange={(e) => setF({ ...f, fecha_programada: e.target.value })} data-testid="egreso-vence-input" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Pagado el (opcional)</Label>
            <Input type="date" value={f.fecha_real} onChange={(e) => setF({ ...f, fecha_real: e.target.value })} data-testid="egreso-pagado-input" /></div>
          <div className="md:col-span-4 flex justify-end">
            <Button type="submit" className="bg-[#002B49] hover:bg-[#00395f] text-white gap-2" data-testid="egreso-guardar-btn"><Plus size={14} /> Capturar egreso</Button>
          </div>
        </form>
        <div className="border rounded-xl overflow-x-auto">
          <table className="w-full text-xs min-w-[780px]">
            <thead><tr className="bg-slate-50 text-left text-[10px] uppercase tracking-wider text-slate-500">
              <th className="px-3 py-2">Categoría</th><th className="px-3 py-2">Concepto</th><th className="px-3 py-2">Contraparte</th>
              <th className="px-3 py-2">Proyecto</th><th className="px-3 py-2 text-right">Monto</th>
              <th className="px-3 py-2">Vence</th><th className="px-3 py-2">Pagado</th><th className="px-3 py-2">Estatus</th><th className="px-3 py-2" /></tr></thead>
            <tbody>
              {(lista?.egresos || []).length === 0 && <tr><td colSpan={9} className="px-3 py-5 text-center text-slate-400" data-testid="egresos-op-vacio">Sin egresos operativos en este periodo.</td></tr>}
              {(lista?.egresos || []).map((x) => (
                <tr key={x.id} className="border-t border-slate-100" data-testid={`egreso-op-row-${x.id}`}>
                  <td className="px-3 py-1.5 first-letter:uppercase text-slate-700">{x.categoria}</td>
                  <td className="px-3 py-1.5 text-slate-600">{x.concepto}</td>
                  <td className="px-3 py-1.5 text-slate-500">{x.contraparte || "—"}</td>
                  <td className="px-3 py-1.5 text-slate-500">{x.proyecto_nombre}</td>
                  <td className="px-3 py-1.5 text-right font-semibold text-[#002B49]">{mxn2(x.monto)}</td>
                  <td className="px-3 py-1.5 text-slate-500">{fDate(x.fecha_programada)}</td>
                  <td className="px-3 py-1.5 text-slate-500">{fDate(x.fecha_real)}</td>
                  <td className="px-3 py-1.5">
                    <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${x.estatus === "realizado" ? "bg-emerald-50 text-emerald-700" : x.estatus === "vencido" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"}`}>{x.estatus}</span>
                  </td>
                  <td className="px-3 py-1.5 text-right whitespace-nowrap">
                    {!x.fecha_real && <button onClick={() => pagar(x)} title="Marcar como pagado hoy" className="text-[#00A3B5] hover:underline mr-2" data-testid={`egreso-pagar-${x.id}`}>Pagar</button>}
                    <button onClick={() => borrar(x)} className="text-slate-300 hover:text-red-500"><Trash2 size={13} /></button>
                  </td>
                </tr>))}
            </tbody>
          </table>
        </div>
        {lista && <p className="text-xs text-slate-500">Total capturado en el periodo: <b className="text-[#002B49]">{mxn2(lista.total)}</b></p>}
      </DialogContent>
    </Dialog>
  );
}

export default function FlujoEfectivo({ sel = [], proyectos = [], onPeriodo }) {
  const [periodo, setPeriodo] = useState("mes");
  const [rango, setRango] = useState({ desde: "", hasta: "" });
  const [d, setD] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [cajaOpen, setCajaOpen] = useState(false);
  const [egresosOpen, setEgresosOpen] = useState(false);

  const qs = () => {
    const p = new URLSearchParams({ periodo });
    if (periodo === "rango") { p.set("desde", rango.desde); p.set("hasta", rango.hasta); }
    if (sel.length) p.set("projects", sel.join(","));
    return `?${p.toString()}`;
  };

  useEffect(() => {
    if (onPeriodo) onPeriodo({ periodo, desde: rango.desde, hasta: rango.hasta });
  }, [periodo, rango.desde, rango.hasta]); // eslint-disable-line

  useEffect(() => {
    if (periodo === "rango" && !(rango.desde && rango.hasta)) return;
    setD(null);
    api.get(`/flujo/resumen${qs()}`).then((r) => setD(r.data)).catch((e) => toast.error(apiError(e)));
  }, [periodo, rango.desde, rango.hasta, sel.join(",")]); // eslint-disable-line

  const recargar = () => api.get(`/flujo/resumen${qs()}`).then((r) => setD(r.data)).catch(() => {});

  const abrir = (titulo, extra) => {
    const p = new URLSearchParams(qs().slice(1));
    Object.entries(extra).forEach(([k, v]) => p.set(k, v));
    setDetalle({ titulo, query: `?${p.toString()}` });
  };

  const exportar = async () => {
    try {
      const r = await api.get(`/flujo/excel${qs()}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `flujo_${periodo}.xlsx`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Listado de flujo exportado");
    } catch (e) { toast.error("No se pudo generar el Excel"); }
  };

  const ing = d?.ingresos, egr = d?.egresos, neto = d?.flujo_neto;

  return (
    <div className="mb-8" data-testid="flujo-efectivo">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#C9A96A] font-bold mr-2">Flujo de efectivo</p>
        {PERIODOS.map(([k, label]) => (
          <button key={k} onClick={() => setPeriodo(k)} data-testid={`flujo-periodo-${k}`}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${periodo === k ? "bg-[#C9A96A] border-[#C9A96A] text-black font-bold" : "border-[#2C2822] text-white/60 hover:border-[#C9A96A]/50"}`}>
            {label}
          </button>
        ))}
        {periodo === "rango" && (
          <div className="flex items-center gap-2">
            <Input type="date" data-testid="flujo-rango-desde" value={rango.desde} onChange={(e) => setRango({ ...rango, desde: e.target.value })}
              className="h-8 w-36 bg-[#1B1915] border-[#2C2822] text-white text-xs" />
            <span className="text-white/30 text-xs">al</span>
            <Input type="date" data-testid="flujo-rango-hasta" value={rango.hasta} onChange={(e) => setRango({ ...rango, hasta: e.target.value })}
              className="h-8 w-36 bg-[#1B1915] border-[#2C2822] text-white text-xs" />
          </div>
        )}
        <div className="flex-1" />
        <button onClick={() => setCajaOpen(true)} data-testid="flujo-caja-btn"
          className="flex items-center gap-2 text-[#EAD8C0] hover:text-white px-3 py-1.5 rounded-md border border-[#EAD8C0]/30 hover:border-[#EAD8C0]/70 text-[11px] font-bold uppercase tracking-wider">
          <Wallet size={13} /> Saldo de caja
        </button>
        <button onClick={() => setEgresosOpen(true)} data-testid="flujo-capturar-egreso-btn"
          className="flex items-center gap-2 text-[#EAD8C0] hover:text-white px-3 py-1.5 rounded-md border border-[#EAD8C0]/30 hover:border-[#EAD8C0]/70 text-[11px] font-bold uppercase tracking-wider">
          <Plus size={13} /> Capturar egreso
        </button>
        <button onClick={exportar} disabled={!d} data-testid="flujo-exportar-excel"
          className="flex items-center gap-2 text-[#EAD8C0] hover:text-white px-3 py-1.5 rounded-md border border-[#EAD8C0]/30 hover:border-[#EAD8C0]/70 text-[11px] font-bold uppercase tracking-wider disabled:opacity-40">
          <FileSpreadsheet size={13} /> Exportar Excel
        </button>
      </div>

      {!d ? <p className="text-white/40 py-10 text-center text-sm">Calculando flujo…</p> : (
        <>
          <p className="text-[11px] text-white/35 mb-3" data-testid="flujo-periodo-etiqueta">
            {d.periodo.etiqueta} · comparado contra {d.periodo.anterior.etiqueta} · {d.total_movimientos} movimientos consolidados
          </p>

          {!d.hay_datos && (
            <div className="border border-[#2C2822] rounded-xl p-6 text-center mb-6" data-testid="flujo-sin-datos">
              <p className="text-white/60 font-semibold">Sin movimientos en este periodo</p>
              <p className="text-xs text-white/30 mt-1">Los ingresos vienen de Cobranza (plan de pagos y pagos de clientes) y de las disposiciones de crédito; los egresos, de las ministraciones y estimaciones de obra y del servicio de deuda.</p>
            </div>
          )}

          {/* BLOQUE A — flujo neto */}
          <div className="rounded-xl border p-5 mb-5 flex flex-col lg:flex-row lg:items-center gap-5"
            data-testid="flujo-neto-card"
            style={{ borderColor: (neto.real >= 0 ? VERDE : ROJO) + "55", background: `linear-gradient(90deg, ${(neto.real >= 0 ? VERDE : ROJO)}14, transparent 60%)` }}>
            <div className="flex items-center gap-3">
              {neto.real >= 0 ? <ArrowUpRight size={22} color={VERDE} /> : <ArrowDownRight size={22} color={ROJO} />}
              <div>
                <p className="text-[10px] uppercase tracking-[0.25em] text-white/50 font-bold">Flujo neto real del periodo</p>
                <p className="text-xs text-white/35">Ingresos cobrados − egresos pagados (solo dinero movido)</p>
              </div>
            </div>
            <div className="flex flex-wrap items-baseline gap-x-10 gap-y-3 lg:ml-auto">
              <div>
                <p className="font-display text-3xl font-bold" style={{ color: neto.real >= 0 ? VERDE : ROJO }} data-testid="flujo-neto-real">{mxn2(neto.real)}</p>
                <Variacion monto={neto.variacion} pct={neto.variacion_pct} />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-white/40">Periodo anterior</p>
                <p className="font-display text-xl font-bold text-[#EAD8C0]">{corto(neto.anterior)}</p>
              </div>
              <div title="Ingresos programados − egresos programados del siguiente periodo equivalente">
                <p className="text-[10px] uppercase tracking-wider text-white/40">Proyectado {d.periodo.siguiente.etiqueta}</p>
                <p className={`font-display text-xl font-bold ${neto.proyectado_siguiente >= 0 ? "text-emerald-400" : "text-red-400"}`} data-testid="flujo-neto-proyectado">{corto(neto.proyectado_siguiente)}</p>
              </div>
              <div title="Saldo inicial capturado + flujo real registrado después de la fecha de corte">
                <p className="text-[10px] uppercase tracking-wider text-white/40">Saldo de caja hoy</p>
                {d.caja.configurada ? (
                  <>
                    <p className="font-display text-xl font-bold text-[#EAD8C0]" data-testid="flujo-caja-saldo">{corto(d.caja.saldo_hoy)}</p>
                    <button onClick={() => setCajaOpen(true)} className="text-[10px] text-white/35 hover:text-white underline">inicial {corto(d.caja.inicial)} · editar</button>
                  </>
                ) : (
                  <button onClick={() => setCajaOpen(true)} className="text-xs text-amber-300 underline" data-testid="flujo-caja-sin-datos">Sin datos — captura el saldo del banco</button>
                )}
              </div>
            </div>
          </div>

          {d.alertas.sin_fecha > 0 && (
            <button onClick={() => abrir("Movimientos sin fecha (hay que corregirlos en su módulo)", { vista: "sin_fecha" })}
              data-testid="flujo-alerta-sin-fecha"
              className="w-full text-left flex items-center gap-2 border border-amber-500/40 bg-amber-500/10 rounded-lg px-4 py-2.5 mb-5 text-xs text-amber-200 hover:border-amber-400">
              <AlertTriangle size={14} /> {d.alertas.sin_fecha} movimiento(s) sin fecha por {corto(d.alertas.monto_sin_fecha)} — excluidos del flujo. Clic para verlos y corregirlos.
            </button>
          )}

          {/* BLOQUE B — INGRESOS */}
          <p className="text-[11px] uppercase tracking-[0.25em] text-white/50 font-bold mb-2 mt-6">Ingresos</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <Tarjeta testId="flujo-ing-real" titulo="Ingresos reales" valor={mxn2(ing.real)} color="text-emerald-300"
              formula="Σ cobros con fecha real dentro del periodo (pagos de clientes + disposiciones de crédito ministradas)"
              onClick={() => abrir("Ingresos reales del periodo", { tipo: "ingreso", vista: "real" })}>
              <Variacion monto={ing.variacion} pct={ing.variacion_pct} />
            </Tarjeta>
            <Tarjeta testId="flujo-ing-prog" titulo="Por cobrar del periodo" valor={mxn2(ing.programado)}
              formula="Exhibiciones y disposiciones con vencimiento dentro del periodo que aún no se han cobrado"
              onClick={() => abrir("Ingresos programados del periodo (aún no cobrados)", { tipo: "ingreso", vista: "programado" })}>
              <p className="text-[11px] text-white/40 mt-0.5">Debía entrar {corto(ing.esperado)} · entró {corto(ing.cumplido_del_esperado)}</p>
              <p className="text-[11px] text-white/40">Cumplimiento: <b className="text-[#EAD8C0]">{ing.cumplimiento_pct != null ? `${ing.cumplimiento_pct}%` : "Sin datos"}</b></p>
            </Tarjeta>
            <Tarjeta testId="flujo-ing-vencido" titulo="Por cobrar vencido" valor={mxn2(ing.vencido.monto)} color="text-red-400" borde="border-red-500/30"
              formula="Σ exhibiciones pendientes con fecha de vencimiento anterior a hoy (cobranza atrasada, sin importar el periodo)"
              onClick={() => abrir("Por cobrar vencido a hoy", { tipo: "ingreso", vista: "vencido" })}>
              <p className="text-[11px] text-white/40 mt-0.5">{ing.vencido.movimientos} movimiento(s)</p>
              <div className="mt-1.5 space-y-0.5">
                {ing.vencido.top.map((t) => (
                  <p key={t.contraparte} className="text-[10px] text-white/45 flex justify-between gap-2">
                    <span className="truncate">{t.contraparte}</span><span className="text-red-300">{corto(t.monto)}</span></p>
                ))}
              </div>
            </Tarjeta>
            <Tarjeta testId="flujo-ing-proximo" titulo={`Por cobrar ${d.periodo.siguiente.etiqueta}`} valor={mxn2(ing.proximo_periodo)}
              formula="Σ programados con vencimiento en el siguiente periodo equivalente"
              onClick={() => abrir("Por cobrar del siguiente periodo", { tipo: "ingreso", vista: "proximo" })}>
              <p className="text-[11px] text-white/40 mt-0.5">{ing.num_movimientos} cobros en el periodo · ticket prom. {corto(ing.ticket_promedio)}</p>
            </Tarjeta>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            <div className="bg-gradient-to-b from-[#1B1915] to-[#141210] border border-[#2C2822] rounded-xl p-5">
              <h4 className="font-display text-base font-bold text-[#F5EFE4] mb-3">Ingresos por categoría</h4>
              <Barras datos={ing.categorias} testId="flujo-ing-categorias" />
            </div>
            <div className="bg-gradient-to-b from-[#1B1915] to-[#141210] border border-[#2C2822] rounded-xl p-5">
              <h4 className="font-display text-base font-bold text-[#F5EFE4] mb-3">Tendencia de ingresos</h4>
              <Tendencia datos={ing.tendencia} colorReal={VERDE} testId="flujo-ing-tendencia" />
            </div>
          </div>

          {/* BLOQUE C — EGRESOS */}
          <p className="text-[11px] uppercase tracking-[0.25em] text-white/50 font-bold mb-2">Egresos</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <Tarjeta testId="flujo-egr-real" titulo="Egresos reales" valor={mxn2(egr.real)} color="text-[#EAD8C0]"
              formula="Σ pagos con fecha real dentro del periodo (ministraciones a contratistas + pagos de crédito)"
              onClick={() => abrir("Egresos reales del periodo", { tipo: "egreso", vista: "real" })}>
              <Variacion monto={-egr.variacion} pct={egr.variacion_pct} />
            </Tarjeta>
            <Tarjeta testId="flujo-egr-prog" titulo="Por pagar del periodo" valor={mxn2(egr.programado)}
              formula="Estimaciones de obra por pagar y amortizaciones con vencimiento dentro del periodo, aún no pagadas"
              onClick={() => abrir("Egresos programados del periodo (aún no pagados)", { tipo: "egreso", vista: "programado" })}>
              <p className="text-[11px] text-white/40 mt-0.5">Debía salir {corto(egr.esperado)} · salió {corto(egr.cumplido_del_esperado)}</p>
              <p className="text-[11px] text-white/40">Ejecutado: <b className="text-[#EAD8C0]">{egr.cumplimiento_pct != null ? `${egr.cumplimiento_pct}%` : "Sin datos"}</b></p>
            </Tarjeta>
            <Tarjeta testId="flujo-egr-vencido" titulo="Por pagar vencido" valor={mxn2(egr.vencido.monto)} color="text-red-400" borde="border-red-500/30"
              formula="Σ egresos pendientes con fecha de vencimiento anterior a hoy"
              onClick={() => abrir("Por pagar vencido a hoy", { tipo: "egreso", vista: "vencido" })}>
              <p className="text-[11px] text-white/40 mt-0.5">{egr.vencido.movimientos} movimiento(s)</p>
              <div className="mt-1.5 space-y-0.5">
                {egr.vencido.top.map((t) => (
                  <p key={t.contraparte} className="text-[10px] text-white/45 flex justify-between gap-2">
                    <span className="truncate">{t.contraparte}</span><span className="text-red-300">{corto(t.monto)}</span></p>
                ))}
              </div>
            </Tarjeta>
            <Tarjeta testId="flujo-egr-proximo" titulo={`Por pagar ${d.periodo.siguiente.etiqueta}`} valor={mxn2(egr.proximo_periodo)}
              formula="Σ egresos programados con vencimiento en el siguiente periodo equivalente"
              onClick={() => abrir("Por pagar del siguiente periodo", { tipo: "egreso", vista: "proximo" })}>
              <p className="text-[11px] text-white/40 mt-0.5">Peso de la construcción: <b className="text-[#EAD8C0]">{egr.peso_construccion_pct != null ? `${egr.peso_construccion_pct}%` : "Sin datos"}</b></p>
            </Tarjeta>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            <div className="bg-gradient-to-b from-[#1B1915] to-[#141210] border border-[#2C2822] rounded-xl p-5">
              <h4 className="font-display text-base font-bold text-[#F5EFE4] mb-3">Egresos por categoría</h4>
              <Barras datos={egr.categorias} testId="flujo-egr-categorias" />
              <button onClick={() => setEgresosOpen(true)} className="text-[11px] text-[#C9A96A] hover:text-white underline mt-3" data-testid="flujo-egr-capturar-link">
                Capturar proveedor, nómina, comisiones, terreno, administrativo, marketing o impuestos
              </button>
            </div>
            <div className="bg-gradient-to-b from-[#1B1915] to-[#141210] border border-[#2C2822] rounded-xl p-5">
              <h4 className="font-display text-base font-bold text-[#F5EFE4] mb-3">Tendencia de egresos</h4>
              <Tendencia datos={egr.tendencia} colorReal={ORO} testId="flujo-egr-tendencia" />
            </div>
          </div>

          {/* BLOQUE D — calendario */}
          <div className="bg-gradient-to-b from-[#1B1915] to-[#141210] border border-[#2C2822] rounded-xl p-5" data-testid="flujo-calendario">
            <h4 className="font-display text-base font-bold text-[#F5EFE4] mb-1 flex items-center gap-2"><CalendarDays size={16} className="text-[#C9A96A]" /> Calendario de flujo — próximas 8 semanas</h4>
            <p className="text-[11px] text-white/35 mb-3">Solo movimientos programados. {d.caja.configurada
              ? <>Parte del saldo de caja de hoy (<b className="text-[#EAD8C0]">{corto(d.caja.saldo_hoy)}</b>).</>
              : <>Parte de cero: captura el <button onClick={() => setCajaOpen(true)} className="underline text-amber-300">saldo inicial de caja</button> para ver el saldo real del banco.</>} Si el acumulado cae bajo cero, esa es la semana donde aprieta la caja.</p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs min-w-[620px]">
                <thead><tr className="text-left text-[10px] uppercase tracking-wider text-white/40 border-b border-[#2C2822]">
                  <th className="py-2">Semana</th><th className="py-2 text-right">Ingresos programados</th>
                  <th className="py-2 text-right">Egresos programados</th><th className="py-2 text-right">Flujo neto</th>
                  <th className="py-2 text-right">Acumulado</th></tr></thead>
                <tbody>
                  {d.calendario.map((f, i) => (
                    <tr key={f.desde} data-testid={`flujo-semana-${i}`}
                      className={`border-b border-[#2C2822]/60 ${f.deficit ? "bg-red-500/10" : ""}`}>
                      <td className="py-2 text-white/70">{f.semana}{f.deficit && <span className="ml-2 text-[9px] font-bold uppercase bg-red-500/20 text-red-300 px-1.5 py-0.5 rounded">Déficit</span>}</td>
                      <td className="py-2 text-right text-emerald-300">{f.ingresos_programados ? mxn(f.ingresos_programados) : "—"}</td>
                      <td className="py-2 text-right text-[#EAD8C0]">{f.egresos_programados ? mxn(f.egresos_programados) : "—"}</td>
                      <td className={`py-2 text-right font-semibold ${f.neto >= 0 ? "text-emerald-300" : "text-red-400"}`}>{f.neto ? mxn(f.neto) : "—"}</td>
                      <td className={`py-2 text-right font-bold ${f.acumulado >= 0 ? "text-[#F5EFE4]" : "text-red-400"}`}>{mxn(f.acumulado)}</td>
                    </tr>))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      <DetalleModal q={detalle} onClose={() => setDetalle(null)} />
      <CajaModal open={cajaOpen} onClose={() => setCajaOpen(false)} onSaved={recargar} />
      <EgresosOperativosModal open={egresosOpen} onClose={() => setEgresosOpen(false)} onSaved={recargar}
        qs={qs()} proyectos={proyectos} />
    </div>
  );
}
